import { API, regCode } from "../lib/api";
import { padNum } from "./NumberPicker";

const Ornament = ({ className }) => (
  <svg viewBox="0 0 40 40" className={className} aria-hidden="true">
    <path d="M2 14 L2 2 L14 2" fill="none" stroke="currentColor" strokeWidth="1" />
    <path d="M6 20 L6 6 L20 6" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.6" />
    <path d="M9 13 L15 13 M12 10 L12 16" stroke="currentColor" strokeWidth="0.6" opacity="0.8" />
  </svg>
);

export const Certificate = ({ record }) => (
  <div className="hy-cert p-8 sm:p-14 relative overflow-hidden" data-testid="certificate-canvas">
    <div className="pointer-events-none absolute inset-0 text-amber-400/40">
      <Ornament className="absolute top-4 left-4 w-8 h-8" />
      <Ornament className="absolute top-4 right-4 w-8 h-8 rotate-90" />
      <Ornament className="absolute bottom-4 right-4 w-8 h-8 rotate-180" />
      <Ornament className="absolute bottom-4 left-4 w-8 h-8 -rotate-90" />
      <svg viewBox="0 0 200 200" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[22rem] h-[22rem] opacity-[0.07] text-amber-300">
        <defs>
          <mask id="hy-crescent">
            <rect width="200" height="200" fill="black" />
            <circle cx="100" cy="100" r="70" fill="white" />
            <circle cx="130" cy="86" r="64" fill="black" />
          </mask>
        </defs>
        <circle cx="100" cy="100" r="70" fill="currentColor" mask="url(#hy-crescent)" />
      </svg>
      <svg viewBox="0 0 120 80" className="absolute bottom-16 left-8 w-40 h-28 opacity-40 text-amber-300 hidden sm:block">
        <polyline points="8,64 30,44 52,58 74,26 96,38 112,14" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.5" />
        {[[8, 64], [30, 44], [52, 58], [74, 26], [96, 38], [112, 14]].map(([x, y]) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" fill="currentColor" />
        ))}
      </svg>
    </div>

    <div className="absolute top-8 right-8 sm:top-12 sm:right-12 text-right" data-testid="certificate-number">
      <div className="text-[0.5rem] sm:text-[0.55rem] uppercase tracking-[0.35em] text-amber-400/70">Registry No.</div>
      <div className="font-display text-3xl sm:text-5xl hy-gold-text leading-none mt-1">
        {padNum(record.registry_number)}
      </div>
      <div className="text-[0.55rem] uppercase tracking-[0.2em] text-slate-400 mt-2">{record.edition} Edition</div>
      <div className="h-px w-full bg-amber-500/40 mt-2" />
    </div>

    <div className="text-center relative pt-16 sm:pt-6">
      <div className="font-display text-2xl sm:text-3xl tracking-[0.4em] hy-gold-text">HYLUNER</div>
      <div className="hy-label mt-3 text-[0.55rem]">The Transparent Celestial Registry</div>
      <div className="hy-rule my-8" />
      <p className="font-serif-accent italic text-slate-300 text-lg">Certificate of Commemorative Registration</p>
      <h2 className="font-display text-3xl sm:text-5xl mt-6 hy-gold-text break-words" data-testid="certificate-dedication">
        {record.dedication_name}
      </h2>
      <p className="text-sm text-slate-400 mt-6 max-w-xl mx-auto">
        Registered in the HYLUNER commemorative registry as a permanent symbolic dedication.
      </p>
    </div>

    <div className="grid sm:grid-cols-2 gap-6 mt-12 text-left relative">
      {[
        ["Registry", regCode(record.registry_number)],
        ["Edition", `${record.edition} EDITION`],
        ["Type", record.registry_type],
        ["Registry ID", record.registry_id],
        ["Certificate ID", record.certificate_id || "—"],
        ["Celestial Object", record.celestial_object_id],
        ["Location", record.location_display],
        ["Registered", (record.registration_date || "").slice(0, 10) || "—"],
      ].map(([k, v]) => (
        <div key={k}>
          <div className="hy-label text-[0.55rem]">{k}</div>
          <div className="font-mono-hy text-sm text-amber-100/90 mt-1 break-all">{v}</div>
        </div>
      ))}
    </div>

    {record.dedication_message && (
      <p className="font-serif-accent italic text-center text-slate-300 mt-10 text-lg relative">“{record.dedication_message}”</p>
    )}

    <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mt-12 pt-8 border-t border-amber-500/20 relative">
      <p className="text-[0.65rem] text-slate-500 max-w-lg leading-relaxed">
        TRANSPARENCY DISCLOSURE — This certificate records a private commemorative dedication within the
        HYLUNER registry. It does not confer legal ownership, government-recognised title, or property
        rights over the Moon, any star, planet or other celestial body. HYLUNER is not an astronomical
        naming authority.
      </p>
      <div className="text-center">
        <img
          src={`${API}/certificate/${record.registry_id}/qr.png`}
          alt="Verification QR"
          className="w-24 h-24 bg-white p-1"
          data-testid="certificate-qr"
        />
        <div className="text-[0.6rem] text-slate-500 mt-2">Scan to verify</div>
      </div>
    </div>
  </div>
);
